import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Divider,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import CakeIcon from '@mui/icons-material/Cake';
import HomeIcon from '@mui/icons-material/Home';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedIcon from '@mui/icons-material/Verified';
import CloseIcon from '@mui/icons-material/Close';
import DiamondIcon from '@mui/icons-material/Diamond';
import './Auth.css';

const glowAnimation = keyframes`
  0% { box-shadow: 0 0 5px rgba(198, 164, 63, 0.3); }
  50% { box-shadow: 0 0 30px rgba(198, 164, 63, 0.6); }
  100% { box-shadow: 0 0 5px rgba(198, 164, 63, 0.3); }
`;

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: '16px',
  boxShadow: '0 30px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(198, 164, 63, 0.2)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #C6A43F, #E5D5AC, #C6A43F)',
    animation: `${glowAnimation} 3s ease-in-out infinite`,
  },
}));

const AnimatedStepIcon = styled(Box)(({ active, completed }) => ({
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  backgroundColor: active || completed ? '#C6A43F' : 'rgba(198, 164, 63, 0.1)',
  color: active || completed ? '#0A0F1E' : '#C6A43F',
  transition: 'all 0.3s ease',
  border: completed ? '2px solid #0A0F1E' : '2px solid transparent',
  animation: active ? `${glowAnimation} 2s ease-in-out infinite` : 'none',
}));

const steps = ['Verify', 'Details', 'Address', 'Welcome'];

const Auth = ({ open, onClose, initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode);
  const [activeStep, setActiveStep] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showConfetti, setShowConfetti] = useState(false);

  const [customerDetails, setCustomerDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    birthDate: '',
    gender: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    password: '',
    confirmPassword: ''
  });

  const DEMO_OTP = '123456';

  useEffect(() => {
    if (otpSent && timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(countdown);
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(countdown);
    }
  }, [otpSent, timer]);

  const handlePhoneSubmit = () => {
    if (phoneNumber.length === 10) {
      setOtpSent(true);
      setTimer(30);
      setCanResend(false);
      
      setSnackbar({
        open: true,
        message: `✨ OTP sent to +91 ${phoneNumber} (Demo: 123456)`,
        severity: 'info'
      });
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }

      if (newOtp.every(digit => digit !== '')) {
        const enteredOtp = newOtp.join('');
        if (enteredOtp === DEMO_OTP) {
          setOtpVerified(true);
          setSnackbar({
            open: true,
            message: '✅ Phone number verified successfully!',
            severity: 'success'
          });
          setTimeout(() => {
            if (mode === 'login') {
              handleLogin();
            } else {
              setActiveStep(1);
            }
          }, 1500);
        } else {
          setSnackbar({
            open: true,
            message: '❌ Invalid OTP. Please try again.',
            severity: 'error'
          });
        }
      }
    }
  };

  const handleResendOTP = () => {
    setOtp(['', '', '', '', '', '']);
    setTimer(30);
    setCanResend(false);
    setSnackbar({
      open: true,
      message: `📱 OTP resent to +91 ${phoneNumber}`,
      severity: 'info'
    });
  };

  const handlePersonalDetailsSubmit = () => {
    if (customerDetails.firstName && customerDetails.email && customerDetails.birthDate) {
      setActiveStep(2);
    }
  };

  const handleAddressSubmit = () => {
    if (customerDetails.addressLine1 && customerDetails.city && customerDetails.state && customerDetails.pincode) {
      setActiveStep(3);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);
    }
  };

  const handleSignupComplete = () => {
    setSnackbar({
      open: true,
      message: '🎉 Welcome to MIRAE! Your account has been created successfully.',
      severity: 'success'
    });
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  const handleLogin = () => {
    setSnackbar({
      open: true,
      message: '✨ Welcome back! Login successful.',
      severity: 'success'
    });
    setTimeout(() => {
      onClose();
    }, 1500);
  };

  const renderPhoneVerification = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="auth-step"
    >
      <Box className="phone-icon-container">
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 5, -5, 0]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <PhoneIcon sx={{ fontSize: 70, color: '#C6A43F' }} />
        </motion.div>
      </Box>
      
      <Typography variant="h4" gutterBottom sx={{ 
        fontFamily: 'Cormorant Garamond', 
        fontWeight: 700,
        color: '#0A0F1E',
        textAlign: 'center'
      }}>
        {mode === 'login' ? 'Welcome Back' : 'Join MIRAE'}
      </Typography>
      
      <Typography variant="body1" sx={{ 
        mb: 4, 
        textAlign: 'center',
        color: '#666',
        fontSize: '1rem'
      }}>
        {mode === 'login' 
          ? 'Login with your registered mobile number' 
          : 'Begin your journey into timeless elegance'}
      </Typography>

      <AnimatePresence mode="wait">
        {!otpSent ? (
          <motion.div
            key="phone-input"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
          >
            <TextField
              fullWidth
              label="Mobile Number"
              variant="outlined"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body1" sx={{ color: '#C6A43F', fontWeight: 600 }}>+91</Typography>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(198, 164, 63, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C6A43F',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#C6A43F',
                    borderWidth: '2px',
                  },
                },
              }}
            />
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                fullWidth
                variant="contained"
                onClick={handlePhoneSubmit}
                disabled={phoneNumber.length !== 10}
                sx={{
                  bgcolor: '#C6A43F',
                  '&:hover': { 
                    bgcolor: '#B3923A',
                    boxShadow: '0 10px 30px rgba(198, 164, 63, 0.4)'
                  },
                  py: 1.8,
                  fontSize: '1rem',
                  fontWeight: 600,
                  letterSpacing: '2px',
                  transition: 'all 0.3s ease',
                }}
              >
                Send OTP
              </Button>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="otp-input"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Typography variant="body2" sx={{ 
              mb: 3, 
              textAlign: 'center',
              color: '#666'
            }}>
              Enter OTP sent to <strong style={{ color: '#C6A43F' }}>+91 {phoneNumber}</strong>
            </Typography>
            
            <Box className="otp-container">
              {otp.map((digit, index) => (
                <motion.div
                  key={index}
                  whileFocus={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <TextField
                    id={`otp-${index}`}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    inputProps={{
                      maxLength: 1,
                      style: { 
                        textAlign: 'center', 
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        color: '#0A0F1E'
                      }
                    }}
                    sx={{
                      width: '55px',
                      mx: 0.5,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: digit ? '#C6A43F' : 'rgba(198, 164, 63, 0.3)',
                          borderWidth: digit ? '2px' : '1px',
                        },
                      },
                    }}
                  />
                </motion.div>
              ))}
            </Box>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: timer > 0 ? '#666' : '#C6A43F' }}>
                {timer > 0 ? `Resend OTP in ${timer}s` : 'Didn\'t receive OTP?'}
              </Typography>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  size="small"
                  onClick={handleResendOTP}
                  disabled={!canResend}
                  sx={{
                    color: '#C6A43F',
                    fontWeight: 600,
                    '&:disabled': {
                      color: 'rgba(198, 164, 63, 0.3)',
                    }
                  }}
                >
                  Resend
                </Button>
              </motion.div>
            </Box>

            {otpVerified && mode === 'login' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleLogin}
                  sx={{
                    mt: 3,
                    bgcolor: '#0A0F1E',
                    '&:hover': { 
                      bgcolor: '#C6A43F',
                      boxShadow: '0 10px 30px rgba(198, 164, 63, 0.4)'
                    },
                    py: 1.8,
                    fontSize: '1rem',
                    fontWeight: 600,
                    letterSpacing: '2px',
                    transition: 'all 0.3s ease',
                  }}
                >
                  Login to MIRAE
                </Button>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <Divider sx={{ my: 4 }}>
        <Chip 
          label="OR" 
          size="small" 
          sx={{ 
            bgcolor: 'rgba(198, 164, 63, 0.1)',
            color: '#C6A43F',
            fontWeight: 600
          }} 
        />
      </Divider>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
          {mode === 'login' ? "Don't have an account?" : "Already have an account?"}
        </Typography>
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Button
            onClick={() => {
              setMode(mode === 'login' ? 'signup' : 'login');
              setOtpSent(false);
              setOtpVerified(false);
              setPhoneNumber('');
              setOtp(['', '', '', '', '', '']);
              setActiveStep(0);
            }}
            sx={{
              color: '#C6A43F',
              fontWeight: 600,
              fontSize: '0.9rem',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(198, 164, 63, 0.04)',
              }
            }}
          >
            {mode === 'login' ? 'Create New Account →' : 'Login Instead →'}
          </Button>
        </motion.div>
      </Box>
    </motion.div>
  );

  const renderPersonalDetails = () => (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.5 }}
      className="auth-step"
    >
      <Typography variant="h4" gutterBottom sx={{ 
        fontFamily: 'Cormorant Garamond', 
        fontWeight: 700,
        color: '#0A0F1E',
        mb: 3
      }}>
        Personal Details
      </Typography>

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <motion.div whileFocus={{ scale: 1.02 }}>
            <TextField
              fullWidth
              label="First Name"
              value={customerDetails.firstName}
              onChange={(e) => setCustomerDetails({ ...customerDetails, firstName: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: '#C6A43F' }} />
                  </InputAdornment>
                ),
              }}
            />
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={customerDetails.lastName}
            onChange={(e) => setCustomerDetails({ ...customerDetails, lastName: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email ID"
            type="email"
            value={customerDetails.email}
            onChange={(e) => setCustomerDetails({ ...customerDetails, email: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: '#C6A43F' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Birth Date"
            type="date"
            value={customerDetails.birthDate}
            onChange={(e) => setCustomerDetails({ ...customerDetails, birthDate: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CakeIcon sx={{ color: '#C6A43F' }} />
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Gender"
            value={customerDetails.gender}
            onChange={(e) => setCustomerDetails({ ...customerDetails, gender: e.target.value })}
            SelectProps={{ native: true }}
          >
            <option value="">Select</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={customerDetails.password}
            onChange={(e) => setCustomerDetails({ ...customerDetails, password: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: '#C6A43F' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={customerDetails.confirmPassword}
            onChange={(e) => setCustomerDetails({ ...customerDetails, confirmPassword: e.target.value })}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
        <motion.div style={{ flex: 1 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setActiveStep(0)}
            sx={{
              borderColor: '#C6A43F',
              color: '#C6A43F',
              py: 1.5,
              '&:hover': {
                borderColor: '#B3923A',
                backgroundColor: 'rgba(198, 164, 63, 0.04)',
              }
            }}
          >
            Back
          </Button>
        </motion.div>
        <motion.div style={{ flex: 1 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handlePersonalDetailsSubmit}
            sx={{
              bgcolor: '#C6A43F',
              '&:hover': { 
                bgcolor: '#B3923A',
                boxShadow: '0 10px 30px rgba(198, 164, 63, 0.4)'
              },
              py: 1.5,
            }}
          >
            Continue
          </Button>
        </motion.div>
      </Box>
    </motion.div>
  );

  const renderAddressDetails = () => (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.5 }}
      className="auth-step"
    >
      <Typography variant="h4" gutterBottom sx={{ 
        fontFamily: 'Cormorant Garamond', 
        fontWeight: 700,
        color: '#0A0F1E',
        mb: 3
      }}>
        Address Information
      </Typography>

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 1"
            value={customerDetails.addressLine1}
            onChange={(e) => setCustomerDetails({ ...customerDetails, addressLine1: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <HomeIcon sx={{ color: '#C6A43F' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 2 (Optional)"
            value={customerDetails.addressLine2}
            onChange={(e) => setCustomerDetails({ ...customerDetails, addressLine2: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="City"
            value={customerDetails.city}
            onChange={(e) => setCustomerDetails({ ...customerDetails, city: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="State"
            value={customerDetails.state}
            onChange={(e) => setCustomerDetails({ ...customerDetails, state: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Pincode"
            value={customerDetails.pincode}
            onChange={(e) => setCustomerDetails({ ...customerDetails, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
        <motion.div style={{ flex: 1 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setActiveStep(1)}
            sx={{
              borderColor: '#C6A43F',
              color: '#C6A43F',
              py: 1.5
            }}
          >
            Back
          </Button>
        </motion.div>
        <motion.div style={{ flex: 1 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleAddressSubmit}
            sx={{
              bgcolor: '#C6A43F',
              '&:hover': { bgcolor: '#B3923A' },
              py: 1.5
            }}
          >
            Continue
          </Button>
        </motion.div>
      </Box>
    </motion.div>
  );

  const renderComplete = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, type: "spring" }}
      className="auth-step"
      sx={{ textAlign: 'center' }}
    >
      <motion.div
        animate={{
          rotate: [0, 360],
          scale: [1, 1.2, 1]
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          repeatType: "reverse"
        }}
        className="success-animation"
      >
        <VerifiedIcon sx={{ fontSize: 100, color: '#4CAF50', mb: 2 }} />
      </motion.div>
      
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <Typography variant="h3" gutterBottom sx={{ 
          fontFamily: 'Cormorant Garamond', 
          fontWeight: 700,
          color: '#C6A43F'
        }}>
          Welcome to MIRAE!
        </Typography>
      </motion.div>
      
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <Typography variant="h6" sx={{ mb: 3, color: '#666' }}>
          Your journey into timeless elegance begins now
        </Typography>
      </motion.div>
      
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.7 }}
      >
        <Box sx={{ 
          mt: 4, 
          p: 4, 
          bgcolor: 'rgba(198, 164, 63, 0.1)',
          borderRadius: '16px',
          border: '1px solid rgba(198, 164, 63, 0.3)'
        }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#0A0F1E' }}>
            {customerDetails.firstName} {customerDetails.lastName}
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', mb: 1 }}>
            {customerDetails.email}
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', mb: 1 }}>
            +91 {phoneNumber}
          </Typography>
          <Typography variant="body1" sx={{ color: '#666' }}>
            {customerDetails.city}, {customerDetails.state}
          </Typography>
        </Box>
      </motion.div>

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.9 }}
      >
        <Button
          fullWidth
          variant="contained"
          onClick={handleSignupComplete}
          sx={{
            mt: 4,
            bgcolor: '#C6A43F',
            '&:hover': { 
              bgcolor: '#B3923A',
              boxShadow: '0 10px 30px rgba(198, 164, 63, 0.4)'
            },
            py: 2,
            fontSize: '1.1rem',
            fontWeight: 600,
            letterSpacing: '3px',
          }}
        >
          Start Your Journey
        </Button>
      </motion.div>
    </motion.div>
  );

  return (
    <>
      <Box className={`auth-modal ${open ? 'open' : ''}`}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="auth-overlay"
          onClick={onClose}
        />
        
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
        >
          <StyledPaper elevation={3} className="auth-paper">
            <motion.div
              whileHover={{ rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <IconButton className="close-button" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </motion.div>

            {mode === 'signup' && activeStep < 3 && (
              <Box sx={{ mb: 4, px: 2 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label, index) => (
                    <Step key={label}>
                      <StepLabel
                        StepIconComponent={(props) => (
                          <AnimatedStepIcon {...props}>
                            {index + 1}
                          </AnimatedStepIcon>
                        )}
                      >
                        {label}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            )}

            <AnimatePresence mode="wait">
              {mode === 'signup' ? (
                <>
                  {activeStep === 0 && renderPhoneVerification()}
                  {activeStep === 1 && renderPersonalDetails()}
                  {activeStep === 2 && renderAddressDetails()}
                  {activeStep === 3 && renderComplete()}
                </>
              ) : (
                renderPhoneVerification()
              )}
            </AnimatePresence>

            {/* Decorative Elements */}
            <Box className="auth-diamond-bg">
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  className="diamond-decoration"
                  animate={{
                    rotate: [0, 360],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 10 + i * 2,
                    repeat: Infinity,
                    delay: i * 0.5,
                  }}
                >
                  <DiamondIcon sx={{ color: 'rgba(198, 164, 63, 0.1)', fontSize: 40 }} />
                </motion.div>
              ))}
            </Box>
          </StyledPaper>
        </motion.div>
      </Box>

      {/* Confetti Animation for Welcome Screen */}
      {showConfetti && (
        <div className="confetti-container">
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              className="confetti"
              initial={{
                top: -20,
                left: Math.random() * 100 + '%',
                rotate: 0,
                opacity: 1
              }}
              animate={{
                top: '100vh',
                rotate: Math.random() * 360,
                opacity: 0
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                delay: Math.random() * 0.5,
                ease: "easeOut"
              }}
              style={{
                backgroundColor: `hsl(${Math.random() * 60 + 30}, 70%, 50%)`,
                width: Math.random() * 10 + 5 + 'px',
                height: Math.random() * 10 + 5 + 'px',
              }}
            />
          ))}
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '8px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            '& .MuiAlert-icon': {
              color: snackbar.severity === 'success' ? '#4CAF50' : '#C6A43F'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Auth;